// Съобщения, които СЕ ВИЖДАТ навсякъде — и на телефон, и на web.
// (Alert.alert на React Native не показва нищо в браузър — затова е този toast.)

import React, { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, Text, View } from 'react-native';
import { t } from './i18n';

interface ToastData {
  id: number;
  type: 'error' | 'success' | 'info';
  message: string;
}

let push: ((t: ToastData) => void) | null = null;

export const showError = (message: string) => push?.({ id: Date.now(), type: 'error', message: message || t('Възникна грешка.') });
export const showSuccess = (message: string) => push?.({ id: Date.now(), type: 'success', message });
export const showInfo = (message: string) => push?.({ id: Date.now(), type: 'info', message });

// Потвърждение със собствен диалог (еднакъв на телефон и уеб). Native
// Alert подрежда двата бутона един до друг и режеше по-дългия етикет;
// тук бутоните са един под друг на цяла ширина и винаги се събират.
type ConfirmReq = { title: string; message: string; confirmLabel: string; onConfirm: () => void; onCancel?: () => void; cancelLabel?: string };
let showConfirm: ((r: ConfirmReq) => void) | null = null;

export function confirmAction(
  title: string,
  message: string,
  confirmLabel: string,
  onConfirm: () => void,
  opts: { onCancel?: () => void; cancelLabel?: string } = {}
): void {
  showConfirm?.({ title, message, confirmLabel, onConfirm, ...opts });
}

const COLORS = { error: '#DC2626', success: '#059669', info: '#7621A6' };
const ICONS = { error: '✕', success: '✓', info: 'ℹ' };

// Монтира се веднъж в App.tsx, над навигацията
export function Toaster() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [confirm, setConfirm] = useState<ConfirmReq | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    push = (t) => {
      setToast(t);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setToast(null), 4000);
    };
    showConfirm = (r) => setConfirm(r);
    return () => {
      push = null;
      showConfirm = null;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return (
    <>
      {confirm && (
        <Modal transparent visible animationType="fade" onRequestClose={() => setConfirm(null)}>
          <Pressable
            onPress={() => {
              const cancel = confirm.onCancel;
              setConfirm(null);
              cancel?.();
            }}
            style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', padding: 24 }}
          >
            <Pressable
              onPress={() => {}}
              style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, width: '100%', maxWidth: 420, alignSelf: 'center' }}
            >
              <Text style={{ fontSize: 18, fontWeight: '700', color: '#14213D', marginBottom: 8 }}>{confirm.title}</Text>
              <Text style={{ fontSize: 15, color: '#374151', lineHeight: 21, marginBottom: 20 }}>{confirm.message}</Text>
              {/* бутоните са един под друг на цяла ширина — винаги се събират */}
              <Pressable
                onPress={() => {
                  const fn = confirm.onConfirm;
                  setConfirm(null);
                  fn();
                }}
                style={{ backgroundColor: COLORS.error, borderRadius: 10, paddingVertical: 14, alignItems: 'center' }}
              >
                <Text style={{ color: '#fff', fontWeight: '700', fontSize: 16 }}>{confirm.confirmLabel}</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  const cancel = confirm.onCancel;
                  setConfirm(null);
                  cancel?.();
                }}
                style={{ paddingVertical: 14, alignItems: 'center', marginTop: 4 }}
              >
                <Text style={{ color: '#6B7280', fontWeight: '600', fontSize: 16 }}>{confirm.cancelLabel ?? t('Отказ')}</Text>
              </Pressable>
            </Pressable>
          </Pressable>
        </Modal>
      )}
      {toast && <ToastView toast={toast} onClose={() => setToast(null)} />}
    </>
  );
}

function ToastView({ toast, onClose }: { toast: ToastData; onClose: () => void }) {
  return (
    <Pressable
      onPress={onClose}
      style={{
        position: 'absolute',
        top: 56,
        left: 16,
        right: 16,
        backgroundColor: COLORS[toast.type],
        borderRadius: 12,
        paddingVertical: 14,
        paddingHorizontal: 16,
        zIndex: 1000,
        elevation: 10,
        shadowColor: '#000',
        shadowOpacity: 0.25,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <Text style={{ color: '#fff', fontSize: 16, fontWeight: '700' }}>{ICONS[toast.type]}</Text>
      <Text style={{ color: '#fff', fontWeight: '600', fontSize: 15, flex: 1 }}>{toast.message}</Text>
    </Pressable>
  );
}
